import { Children, cloneElement, forwardRef, isValidElement, type ReactElement } from 'react';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

/**
 * DESIGN_SYSTEM.md §5.1. The primary action has **no hue**: it is ink on graphite, so that
 * when green or red appears on screen it is always data. There is no success-coloured
 * button — `success` is accepted by the contract and renders as the ink primary.
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  /** Contract alias. Renders as `primary` — §5.1: "There is no success-colored button." */
  | 'success'
  /** §5.1 `destructive-outline` — for actions that realize money now ("Close at market"). */
  | 'danger-outline';

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  /** Shows a spinner in the leading slot, sets `disabled` + `aria-busy`. Width never jumps. */
  loading?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
  /** Render the single child element as the button (for `<Link>`), merging classes and props. */
  asChild?: boolean;
};

const VARIANT: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-ink text-ink-inverse border border-transparent',
    'hover:brightness-[0.92] active:brightness-[0.85]',
    'disabled:bg-surface-2 disabled:text-ink-disabled disabled:brightness-100',
  ),
  success: cn(
    'bg-ink text-ink-inverse border border-transparent',
    'hover:brightness-[0.92] active:brightness-[0.85]',
    'disabled:bg-surface-2 disabled:text-ink-disabled disabled:brightness-100',
  ),
  secondary: cn(
    'bg-transparent text-ink border border-border',
    'hover:bg-wash-hover hover:border-border-strong active:bg-wash-pressed',
    'disabled:border-border-subtle disabled:text-ink-disabled disabled:bg-transparent',
  ),
  ghost: cn(
    'bg-transparent text-ink-secondary border border-transparent',
    'hover:bg-wash-hover hover:text-ink active:bg-wash-pressed',
    'disabled:text-ink-disabled disabled:bg-transparent',
  ),
  danger: cn(
    'bg-loss-strong text-white border border-transparent',
    'hover:brightness-[0.92] active:brightness-[0.85]',
    'disabled:bg-surface-2 disabled:text-ink-disabled disabled:brightness-100',
  ),
  'danger-outline': cn(
    'bg-transparent text-loss border border-loss',
    'hover:bg-loss-tint active:bg-loss-tint active:brightness-[0.95]',
    'disabled:border-border-subtle disabled:text-ink-disabled disabled:bg-transparent',
  ),
};

const SIZE: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-control-sm px-[10px] text-caption font-medium gap-1.5',
  md: 'h-control px-3 text-label gap-1.5',
  lg: 'h-control-lg px-4 text-body-md font-medium gap-2',
};

const SPINNER_SIZE: Record<NonNullable<ButtonProps['size']>, 'sm' | 'md'> = {
  sm: 'sm',
  md: 'sm',
  lg: 'md',
};

const BASE = cn(
  'relative inline-flex items-center justify-center whitespace-nowrap rounded-sm',
  // Turkish strings run 20–35% longer: min-width, never fixed width (§3.3).
  'select-none transition-[background-color,border-color,color,filter] duration-fast ease-out',
  'focus-visible:outline-none focus-visible:shadow-focus',
  'disabled:cursor-not-allowed',
);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    iconLeft,
    iconRight,
    fullWidth = false,
    asChild = false,
    className,
    children,
    disabled,
    type,
    ...rest
  },
  ref,
) {
  const classes = cn(BASE, VARIANT[variant], SIZE[size], fullWidth && 'w-full', className);

  // `asChild` lets a router <Link> wear the button skin without nesting an <a> in a <button>.
  if (asChild) {
    const child = Children.only(children);
    if (!isValidElement(child)) return null;
    const element = child as ReactElement<{ className?: string }>;
    return cloneElement(element, {
      className: cn(classes, element.props.className),
    });
  }

  const leading = loading ? <Spinner size={SPINNER_SIZE[size]} /> : iconLeft;

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      className={classes}
      {...rest}
    >
      {leading != null && (
        <span className="inline-flex shrink-0 items-center [&_svg]:h-4 [&_svg]:w-4">{leading}</span>
      )}
      {children}
      {iconRight != null && (
        <span className="inline-flex shrink-0 items-center [&_svg]:h-4 [&_svg]:w-4">
          {iconRight}
        </span>
      )}
    </button>
  );
});
